import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { Trash2, Plus, Minus, ArrowRight, ShoppingCart } from 'lucide-react';

const CartScreen = () => {
  const { cart, updateCartQuantity, removeFromCart, getCartTotal } = useStore();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <ShoppingCart size={40} className="text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-black">Your cart is empty</h2>
        <p className="text-gray-500 mb-8">Looks like you haven't added anything to your cart yet.</p>
        <Link 
          to="/store"
          className="bg-black text-white px-8 py-4 font-bold tracking-widest uppercase hover:bg-gray-900 transition-colors"
        >
          Start Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-12">
      {/* Cart Items */}
      <div className="flex-1">
        <h1 className="text-2xl font-bold tracking-widest uppercase mb-8 border-b pb-4">Shopping Cart</h1>
        <div className="space-y-6">
          {cart.map((item) => (
            <div key={item.product.id} className="flex gap-6 py-4 border-b">
              <div className="w-24 h-32 bg-gray-100 flex-shrink-0">
                {item.product.images && item.product.images.length > 0 ? (
                  <img src={item.product.images[0].image} alt={item.product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Img</div>
                )}
              </div>
              
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-start">
                    <Link to={`/store/product/${item.product.id}`} className="text-lg font-semibold hover:underline line-clamp-2 pr-4">
                      {item.product.name}
                    </Link>
                    <button 
                      onClick={() => removeFromCart(item.product.id, item.variant_detail?.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove Item"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                  <span className="text-xs font-bold tracking-widest text-gray-500 uppercase mt-1 block">
                    {item.product.category_detail?.name || 'GENERAL'}
                  </span>
                  {item.variant_detail && (
                    <span className="text-sm font-medium text-gray-600 block mt-1">
                      Size: {item.variant_detail.size}
                    </span>
                  )}
                </div>
                
                <div className="flex justify-between items-end mt-4">
                  <div className="flex items-center border border-gray-300">
                    <button 
                      onClick={() => updateCartQuantity(item.product.id, item.quantity - 1, item.variant_detail?.id)}
                      className="p-2 hover:bg-gray-100 transition-colors text-gray-600"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-4 font-semibold text-sm w-12 text-center">{item.quantity}</span>
                    <button 
                      onClick={() => updateCartQuantity(item.product.id, item.quantity + 1, item.variant_detail?.id)}
                      className="p-2 hover:bg-gray-100 transition-colors text-gray-600"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="text-lg font-bold text-black">
                    ₵{((item.variant_detail ? parseFloat(item.variant_detail.price) : parseFloat(item.product.price)) * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Summary */}
      <div className="w-full lg:w-96 flex-shrink-0">
        <div className="bg-gray-50 p-6 sm:p-8 rounded-none border border-gray-200 sticky top-24">
          <h2 className="text-lg font-bold tracking-widest uppercase mb-6 pb-4 border-b border-gray-300">Order Summary</h2>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>₵{getCartTotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
          </div>
          
          <div className="border-t border-gray-300 pt-4 mb-8">
            <div className="flex justify-between items-center">
              <span className="font-bold uppercase tracking-wider">Total</span>
              <span className="text-2xl font-bold text-black">₵{getCartTotal().toFixed(2)}</span>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/store/checkout')}
            className="w-full bg-black text-white py-4 px-6 flex items-center justify-between font-bold tracking-widest uppercase hover:bg-gray-900 transition-colors group"
          >
            <span>Checkout</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartScreen;
